import { SvgLogo } from "@/shared/ui/svg"
import { useResetProcessPasswordForm } from "./model"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { TypographyH2, TypographyP } from "@/components/ui/typography"
import { Controller } from "react-hook-form"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupError,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { Lock } from "lucide-react"
import { useAutoTranslation } from "@/entities"
import { LANG_KEYS } from "@/shared/constants"
import styles from "./styles.module.scss"

export const ResetProcessPasswordForm = ({
  email,
  code,
}: {
  email: string
  code: string
}) => {
  const { control, handleSubmit, isLoading, errors } =
    useResetProcessPasswordForm(code)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { t } = useAutoTranslation()

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
      <SvgLogo className="h-16 w-16" variant="enhanced" />

      <form className={styles.form} onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="text-center">
            <TypographyH2>{t(LANG_KEYS.RESET_YOUR_PASSWORD)}</TypographyH2>
            <TypographyP className="text-muted-foreground">{email}</TypographyP>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              <Controller
                control={control}
                name="password"
                rules={{
                  required: {
                    value: true,
                    message: t(LANG_KEYS.REQUIRED),
                  },
                  minLength: {
                    value: 6,
                    message: t(LANG_KEYS.MIN_PASS_LENGTH),
                  },
                }}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="select-password">
                      {t(LANG_KEYS.PASSWORD)}
                    </Label>
                    <InputGroup>
                      <InputGroupInput
                        type={showPassword ? "text" : "password"}
                        id="select-password"
                        value={field.value}
                        onChange={field.onChange}
                        aria-invalid={!!errors.password}
                      />
                      <InputGroupAddon align="inline-end">
                        <Button
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          size="icon"
                          className="hover:bg-transparent"
                        >
                          {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                        </Button>
                      </InputGroupAddon>
                      <InputGroupAddon>
                        <Lock />
                      </InputGroupAddon>
                      <InputGroupError>
                        {errors.password?.message}
                      </InputGroupError>
                    </InputGroup>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="confirmPassword"
                rules={{
                  required: {
                    value: true,
                    message: t(LANG_KEYS.REQUIRED),
                  },
                  minLength: {
                    value: 6,
                    message: t(LANG_KEYS.MIN_PASS_LENGTH),
                  },
                }}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="select-confirm-password">
                      {t(LANG_KEYS.CONFIRM_PASSWORD)}
                    </Label>
                    <InputGroup>
                      <InputGroupInput
                        type={showConfirmPassword ? "text" : "password"}
                        id="select-confirm-password"
                        value={field.value}
                        onChange={field.onChange}
                        aria-invalid={!!errors.confirmPassword}
                      />
                      <InputGroupAddon align="inline-end">
                        <Button
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          variant="ghost"
                          size="icon"
                          className="hover:bg-transparent"
                        >
                          {showConfirmPassword ? <EyeIcon /> : <EyeOffIcon />}
                        </Button>
                      </InputGroupAddon>
                      <InputGroupAddon>
                        <Lock />
                      </InputGroupAddon>
                      <InputGroupError>
                        {errors.confirmPassword?.message}
                      </InputGroupError>
                    </InputGroup>
                  </div>
                )}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" isLoading={isLoading} className="w-full">
              {t(LANG_KEYS.CONFIRM)}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}
